import { LIFTABLE_ACTIONS, isModerationAction, type LiftableAction, type ModerationAction } from "odyssey-shared";
import { timeoutSignal } from "../../timeout-signal.ts";
import { say } from "../../words.ts";

/**
 * How a GM's page reaches the gateway's `/moderation` (ADR-056's amendment of 2026-10-04, item 5): an action is a
 * JSON post and a page of the list a GET, each carrying the page's current session token as `Authorization:
 * Bearer`, read at the moment of sending so a refreshed session's token is the one presented, and no cookie, since
 * the route reads none. The gateway alone decides who may. A refusal whose code has one sentence is worded here from
 * the code (`WORDED_REFUSALS`, ADR-119 decision 3); any other is the gateway's own sentence, shown as it comes. Every
 * answer is read against its shape, so a field out of place is dropped rather than drawn.
 *
 * A lift may name the one action it ends (ADR-056's amendment of 2026-10-06, decisions 9 and 12): `lifts`, a
 * standing row's id, as a row's own End button sends, or `ends`, `ban` or `freeze`, as `/unban` and `/unfreeze`
 * send. The page is published before the gateway is replaced, so a page may meet a gateway whose schema does not
 * know the two fields and answers 400 `body`. Nothing ended there, the refusal carries the page's sentence saying
 * so, and no request is ever sent again without the field, since that would end more than the GM asked: each call
 * to `act` sends one request.
 */

/**
 * An action as the page asks for it: `minutes` undefined for an action with no length or a freeze of the default
 * length, 0 for a permanent ban; `note` null for none; and, on a lift alone and never both, `lifts`, the id of the
 * standing row to end, or `ends`, the kind to end. A lift with neither ends every ban and freeze that stands.
 */
export interface ActionBody {
  readonly action: ModerationAction;
  readonly familyName: string;
  readonly minutes: number | undefined;
  readonly reason: string;
  readonly note: string | null;
  readonly lifts?: string;
  readonly ends?: LiftableAction;
}

/**
 * The refusal codes the page words itself, as `moderation.refused.<code>`: each has one sentence whatever was asked.
 * The gateway words `standing`, `none`, `active` and `duration` by the action asked for, and `unavailable` and
 * `server` by the route, so those are shown as its `message`, as is a code this build does not know.
 */
export const WORDED_REFUSALS = ["session", "forbidden", "family", "own", "offline", "protected", "body", "reason", "note", "rate"] as const;

/**
 * Whether the page words a refusal's code itself.
 *
 * @param error The code as the gateway sent it.
 * @returns True for a code of `WORDED_REFUSALS`.
 */
function isWorded(error: string): error is (typeof WORDED_REFUSALS)[number] {
  return (WORDED_REFUSALS as readonly string[]).includes(error);
}

/**
 * A refusal: its code and its sentence, the page's from the code or the gateway's own; or the page's own sentence
 * with no code when the gateway gave no usable answer.
 */
export interface Refused {
  readonly ok: false;
  readonly code: string;
  readonly message: string;
}

/**
 * One fact of the fight a frozen character was in, as the gateway answers it: true, false, or the word `unknown` when
 * the map server could not say.
 */
export type FightFact = boolean | "unknown";

/**
 * Whether a field of an answer is a fact of a fight.
 *
 * @param value The field.
 * @returns True for true, false and `unknown`.
 */
function isFightFact(value: unknown): value is FightFact {
  return typeof value === "boolean" || value === "unknown";
}

/**
 * The answer to an action: the row written, the family as the gateway holds it, the end (null for none), how many
 * actions a lift ended (`lifted`) and their kinds, a ban before a freeze (`ended`: empty for an action that ends
 * nothing, null when the gateway named none, as one that does not send the field), and for a freeze the fight the
 * character was in as it was written, each fact true, false or `unknown` and absent from a gateway that sends
 * neither; or the refusal.
 */
export type ActionReply = { readonly ok: true; readonly id: string; readonly action: ModerationAction; readonly familyName: string; readonly until: number | null; readonly lifted: number; readonly ended: readonly LiftableAction[] | null; readonly inCombat?: FightFact; readonly combatant?: FightFact } | Refused;

/**
 * One row of the list as the gateway sends it, with whether this GM may end it; a gateway from before the field
 * names none, and the row is then offered its End, which the gateway decides.
 */
export interface ListedRow {
  readonly id: string;
  readonly action: ModerationAction;
  readonly familyName: string;
  readonly issuer: string;
  readonly at: number;
  readonly until: number | null;
  readonly reason: string;
  readonly note: string | null;
  readonly state: "active" | "ended" | "lifted" | "done";
  readonly mayEnd: boolean;
}

/**
 * The answer to a request for a page of the list: its rows, newest first, and the id to ask the next page with,
 * null when this is the last; or the refusal.
 */
export type ListReply = { readonly ok: true; readonly actions: readonly ListedRow[]; readonly next: string | null } | Refused;

/**
 * Which rows a page of the list holds.
 */
export type ListScope = "all" | "active";

/**
 * The client.
 */
export interface ModerationClient {
  readonly act: (body: ActionBody) => Promise<ActionReply>;
  readonly list: (scope: ListScope, before: string | null) => Promise<ListReply>;
}

/**
 * What the client runs with: the gateway's `/moderation` URL, the page's current session token (null when it
 * holds none), and the fetch, the page's own unless a test gives another.
 */
export interface ModerationClientOptions {
  readonly endpoint: string;
  readonly token: () => string | null;
  readonly fetch?: (input: string, init?: RequestInit) => Promise<Response>;
}

/**
 * How long a request may take before the page gives up on it as a server that did not answer.
 */
const REQUEST_TIMEOUT_MS = 10_000;

const STATES: ReadonlySet<string> = new Set(["active", "ended", "lifted", "done"]);

/**
 * Reads one row of the list against its shape.
 *
 * @param value The row as sent.
 * @returns The row, or null when a field is missing or of another type, or its action or state is one this build
 *   does not know.
 */
function listedRow(value: unknown): ListedRow | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const { id, action, familyName, issuer, at, until, reason, note, state, mayEnd } = value as Record<string, unknown>;
  if (typeof id !== "string" || typeof action !== "string" || !isModerationAction(action) || typeof familyName !== "string" || typeof issuer !== "string" || typeof at !== "number") {
    return null;
  }
  if ((until !== null && typeof until !== "number") || typeof reason !== "string" || (note !== null && typeof note !== "string") || typeof state !== "string" || !STATES.has(state)) {
    return null;
  }
  return { id, action, familyName, issuer, at, until, reason, note, state: state as ListedRow["state"], mayEnd: typeof mayEnd === "boolean" ? mayEnd : true };
}

/**
 * Builds the client.
 *
 * @param options The endpoint, the token and the fetch.
 * @returns The client; its calls never reject.
 */
export function moderationClient(options: ModerationClientOptions): ModerationClient {
  const send = options.fetch ?? ((input: string, init?: RequestInit) => fetch(input, init));
  /**
   * Sends one request and reads its JSON answer.
   *
   * @param url The URL.
   * @param init The method and, for a post, the body.
   * @returns The status and the parsed body, null when it is no JSON object; or the refusal for a page with no
   *   token or a server that did not answer.
   */
  const ask = async (url: string, init: RequestInit): Promise<{ readonly status: number; readonly body: Record<string, unknown> | null } | Refused> => {
    const token = options.token();
    if (token === null || token === "") {
      return { ok: false, code: "session", message: say("moderation.client.session") };
    }
    const limit = timeoutSignal(REQUEST_TIMEOUT_MS);
    try {
      const response = await send(url, { ...init, headers: { ...(init.body === undefined ? {} : { "Content-Type": "application/json" }), Authorization: `Bearer ${token}` }, credentials: "omit", signal: limit.signal });
      let parsed: unknown = null;
      try {
        parsed = await response.json();
      } catch {
        parsed = null;
      }
      return { status: response.status, body: typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null };
    } catch {
      return { ok: false, code: "", message: say("moderation.client.unreachable") };
    } finally {
      limit.settle();
    }
  };
  /**
   * The refusal an answer carries: the gateway's code with the page's sentence for it or, for a code the page does
   * not word, the gateway's; or the page's sentence for the status.
   *
   * @param status The status.
   * @param body The parsed body, or null.
   * @returns The refusal.
   */
  const refusal = (status: number, body: Record<string, unknown> | null): Refused => {
    const message = body?.["message"];
    const error = body?.["error"];
    return typeof message === "string" && typeof error === "string" ? { ok: false, code: error, message: isWorded(error) ? say(`moderation.refused.${error}`) : message } : { ok: false, code: "", message: say("moderation.client.failed", { status: String(status) }) };
  };
  return {
    act: async (body) => {
      const named = body.lifts !== undefined || body.ends !== undefined;
      const sent = { action: body.action, familyName: body.familyName, ...(body.minutes === undefined ? {} : { minutes: body.minutes }), reason: body.reason, ...(body.note === null ? {} : { note: body.note }), ...(body.lifts === undefined ? {} : { lifts: body.lifts }), ...(body.ends === undefined ? {} : { ends: body.ends }) };
      const answer = await ask(options.endpoint, { method: "POST", body: JSON.stringify(sent) });
      if ("ok" in answer) {
        return answer;
      }
      const { id, action, familyName, until, lifted, ended, inCombat, combatant } = answer.body ?? {};
      if (answer.status !== 200 || typeof id !== "string" || typeof action !== "string" || !isModerationAction(action) || typeof familyName !== "string" || (until !== null && typeof until !== "number") || typeof lifted !== "number") {
        const refused = refusal(answer.status, answer.status === 200 ? null : answer.body);
        return named && answer.status === 400 && refused.code === "body" ? { ...refused, message: say("moderation.client.cannotEndOne") } : refused;
      }
      const fight = { ...(isFightFact(inCombat) ? { inCombat } : {}), ...(isFightFact(combatant) ? { combatant } : {}) };
      return { ok: true, id, action, familyName, until, lifted, ended: Array.isArray(ended) ? LIFTABLE_ACTIONS.filter((kind) => ended.includes(kind)) : null, ...fight };
    },
    list: async (scope, before) => {
      const answer = await ask(`${options.endpoint}?scope=${scope}${before === null ? "" : `&before=${encodeURIComponent(before)}`}`, { method: "GET" });
      if ("ok" in answer) {
        return answer;
      }
      const { actions, next } = answer.body ?? {};
      if (answer.status !== 200 || !Array.isArray(actions) || (next !== null && typeof next !== "string")) {
        return refusal(answer.status, answer.status === 200 ? null : answer.body);
      }
      return { ok: true, actions: actions.map(listedRow).filter((row): row is ListedRow => row !== null), next };
    },
  };
}
